import React, { useEffect, useId, useRef, useState } from 'react';
import { useOptionalLifeOS } from './LifeOSProvider';
import { lifeOSClassNames, lifeOSItemTitle, lifeOSStatusLabel } from './presentation';
import './life-os.css';

const DAYS = [
  ['1', 'Seg'], ['2', 'Ter'], ['3', 'Qua'], ['4', 'Qui'], ['5', 'Sex'], ['6', 'Sáb'], ['7', 'Dom'],
];

function localDate() {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
    const value = Object.fromEntries(parts.map(part => [part.type, part.value]));
    return `${value.year}-${value.month}-${value.day}`;
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

/**
 * Explicit routine controls. A skip is an occurrence override; it never
 * silently rewrites the underlying routine definition.
 */
export function RoutinePanel({ className, title = 'Rotinas', autoLoad = true, onClose, onChanged }) {
  const panelId = useId();
  const lifeOS = useOptionalLifeOS();
  const [form, setForm] = useState({ title: '', start_time: '09:00', duration_minutes: 60, flexibility_minutes: 0, priority: 'normal', category: 'general', days_of_week: ['1', '2', '3', '4', '5'] });
  const [notice, setNotice] = useState('');
  const [editingId, setEditingId] = useState(null);
  const attemptedRef = useRef(false);
  const routines = lifeOS?.routines?.items || [];
  const busy = Boolean(lifeOS?.routineAction?.loading);

  useEffect(() => {
    if (!autoLoad || !lifeOS || attemptedRef.current) return;
    attemptedRef.current = true;
    void lifeOS.loadRoutines();
  }, [autoLoad, lifeOS]);

  function setField(name, value) {
    setForm(previous => ({ ...previous, [name]: value }));
  }

  function toggleDay(day) {
    setForm(previous => ({
      ...previous,
      days_of_week: previous.days_of_week.includes(day)
        ? previous.days_of_week.filter(value => value !== day)
        : [...previous.days_of_week, day].sort(),
    }));
  }

  async function save(event) {
    event.preventDefault();
    if (!lifeOS || busy || !form.title.trim() || form.days_of_week.length === 0) return;
    setNotice('');
    const payload = {
      title: form.title.trim(),
      start_time: form.start_time,
      duration_minutes: Number(form.duration_minutes),
      flexibility_minutes: Number(form.flexibility_minutes),
      priority: form.priority,
      category: form.category.trim() || 'general',
      days_of_week: form.days_of_week.map(Number),
    };
    try {
      const response = editingId
        ? await lifeOS.updateRoutine(editingId, payload)
        : await lifeOS.createRoutine(payload);
      setNotice(editingId ? 'Rotina atualizada.' : 'Rotina criada.');
      setEditingId(null);
      setForm({ title: '', start_time: '09:00', duration_minutes: 60, flexibility_minutes: 0, priority: 'normal', category: 'general', days_of_week: ['1', '2', '3', '4', '5'] });
      await lifeOS.loadRoutines();
      await lifeOS.loadToday({ preferCache: false });
      onChanged?.(response);
    } catch {
      setNotice(lifeOS.routineAction?.error || 'Não foi possível salvar esta rotina.');
    }
  }

  function edit(routine) {
    const days = Array.isArray(routine?.days_of_week) ? routine.days_of_week.map(String) : ['1', '2', '3', '4', '5'];
    setEditingId(routine.id);
    setForm({
      title: lifeOSItemTitle(routine),
      start_time: routine.start_time || '09:00',
      duration_minutes: Number(routine.duration_minutes || 60),
      flexibility_minutes: Number(routine.flexibility_minutes || 0),
      priority: routine.priority || 'normal',
      category: routine.category || 'general',
      days_of_week: days.length ? days : ['1', '2', '3', '4', '5'],
    });
  }

  async function skip(routine) {
    if (!lifeOS || busy) return;
    setNotice('');
    try {
      const response = await lifeOS.skipRoutine(routine.id, { date: localDate() });
      setNotice('Rotina pulada somente hoje.');
      await lifeOS.loadRoutines();
      await lifeOS.loadToday({ preferCache: false });
      onChanged?.(response);
    } catch {
      setNotice(lifeOS.routineAction?.error || 'Não foi possível pular esta rotina.');
    }
  }

  return (
    <section className={lifeOSClassNames('life-os-panel', 'life-os-routines', className)} aria-labelledby={panelId}>
      <header className="life-os-panel__header">
        <div>
          <p className="life-os-eyebrow">ATHENA ROUTINES</p>
          <h2 id={panelId}>{title}</h2>
          <p className="life-os-panel__subheading">Blocos recorrentes não são eventos de calendário.</p>
        </div>
        <div className="life-os-panel__tools">
          <button type="button" className="life-os-icon-button" onClick={() => { void lifeOS?.loadRoutines(); }} disabled={busy} aria-label="Atualizar rotinas">↻</button>
          {onClose && <button type="button" className="life-os-icon-button" onClick={onClose} aria-label="Fechar rotinas">×</button>}
        </div>
      </header>

      <form className="life-os-routine-form" onSubmit={save}>
        <label>
          <span>Nome</span>
          <input value={form.title} onChange={event => setField('title', event.target.value)} maxLength={160} placeholder="Ex.: Preparar o dia" required disabled={busy} />
        </label>
        <div className="life-os-routine-form__grid">
          <label><span>Horário</span><input type="time" value={form.start_time} onChange={event => setField('start_time', event.target.value)} required disabled={busy} /></label>
          <label><span>Duração (min)</span><input type="number" min="5" max="720" value={form.duration_minutes} onChange={event => setField('duration_minutes', event.target.value)} required disabled={busy} /></label>
          <label><span>Flexibilidade (min)</span><input type="number" min="0" max="360" value={form.flexibility_minutes} onChange={event => setField('flexibility_minutes', event.target.value)} required disabled={busy} /></label>
          <label><span>Prioridade</span><select value={form.priority} onChange={event => setField('priority', event.target.value)} disabled={busy}><option value="low">Baixa</option><option value="normal">Normal</option><option value="high">Alta</option><option value="urgent">Urgente</option></select></label>
        </div>
        <label><span>Categoria</span><input value={form.category} onChange={event => setField('category', event.target.value)} maxLength={80} placeholder="Pessoal, trabalho…" disabled={busy} /></label>
        <fieldset className="life-os-routine-days"><legend>Dias</legend>{DAYS.map(([day, label]) => <label key={day}><input type="checkbox" checked={form.days_of_week.includes(day)} onChange={() => toggleDay(day)} disabled={busy} /><span>{label}</span></label>)}</fieldset>
        <div className="life-os-routine-form__footer">
          <span aria-live="polite">{notice || lifeOS?.routineAction?.error || ''}</span>
          {editingId && <button type="button" className="life-os-button life-os-button--quiet" onClick={() => { setEditingId(null); setForm({ title: '', start_time: '09:00', duration_minutes: 60, flexibility_minutes: 0, priority: 'normal', category: 'general', days_of_week: ['1', '2', '3', '4', '5'] }); }} disabled={busy}>Cancelar</button>}
          <button type="submit" className="life-os-button" disabled={busy || !form.title.trim() || form.days_of_week.length === 0}>{busy ? 'Salvando…' : editingId ? 'Atualizar' : 'Criar rotina'}</button>
        </div>
      </form>

      <div className="life-os-routine-list" aria-label="Rotinas existentes">
        {lifeOS?.routines?.loading && routines.length === 0 && <div className="life-os-loading-list" role="status"><div className="life-os-skeleton life-os-skeleton--item" /><span className="life-os-sr-only">Carregando rotinas…</span></div>}
        {!lifeOS?.routines?.loading && routines.length === 0 && <div className="life-os-empty"><p>Nenhuma rotina criada ainda.</p></div>}
        {routines.map(routine => (
          <article className="life-os-routine-card" key={routine.id}>
            <div><h3>{lifeOSItemTitle(routine)}</h3><p>{routine.start_time} · {routine.duration_minutes} min · {lifeOSStatusLabel(routine.status)}</p></div>
            <div><button type="button" className="life-os-text-action" onClick={() => edit(routine)} disabled={busy}>Editar</button><button type="button" className="life-os-text-action" onClick={() => { void skip(routine); }} disabled={busy}>Pular hoje</button></div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default RoutinePanel;
